/**
 * `<RevokeApiKeyDialog>` — confirm before revoking an API key. Once
 * confirmed, calls to the API using that key start failing with
 * INVALID_API_KEY immediately.
 */

import { authClient, type AuthClientError } from "@cometchat-ai/auth/client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { formatApiError } from "../lib/format-error";

export interface RevokeApiKeyDialogProps {
  workspaceSlug: string;
  apiKeyId: string;
  apiKeyName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RevokeApiKeyDialog({
  workspaceSlug,
  apiKeyId,
  apiKeyName,
  open,
  onOpenChange,
}: RevokeApiKeyDialogProps) {
  const { t } = useTranslation("apikeys");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => authClient.revokeApiKey(workspaceSlug, apiKeyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["api-keys", workspaceSlug] });
      toast.success(t("revokeDialog.toast"));
      onOpenChange(false);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("revokeDialog.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent
        className="max-w-md gap-0 rounded-3xl p-5"
        data-testid={`revoke-api-key-dialog-${apiKeyId}`}
      >
        <AlertDialogHeader className="space-y-1 text-left">
          <AlertDialogTitle className="text-base font-medium tracking-[0.01em]">
            {t("revokeDialog.confirmTitle", { name: apiKeyName })}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[13px] text-muted-foreground">
            {t("revokeDialog.confirmBody")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-row gap-2 pt-4">
          <AlertDialogCancel
            disabled={mutation.isPending}
            className="mt-0 h-8 flex-1 rounded-md text-[13px]"
            data-testid={`revoke-api-key-cancel-${apiKeyId}`}
          >
            {t("revokeDialog.confirmNo")}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              mutation.mutate();
            }}
            disabled={mutation.isPending}
            className="h-8 flex-1 rounded-md bg-destructive text-[13px] text-destructive-foreground hover:bg-destructive/90"
            data-testid={`revoke-api-key-confirm-${apiKeyId}`}
          >
            {t("revokeDialog.confirmYes")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
